import { Link, Typography } from '@material-ui/core';
import { WarningRounded } from '@material-ui/icons';
import {
  Color,
  Column,
  Columns,
  Inline,
  renderChildren,
  Stack,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { ReactElement, useState } from 'react';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { formatNumber, formatPlural } from 'shared/helpers/IntlHelpers';
import { WeightIcon } from 'shared/icons/WeightIcon';
import {
  PostingLoadDTO,
  PostingVehicleDTO,
} from '../../data/dto/PostingLoadDTO';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import { PostingVehicle } from './PostingVehicle';
import { ScrollableViewport } from './ScrollableViewport';

function formatVehicleLength(length: number) {
  return formatPlural(length, 'vehicle', 'vehicles');
}

function renderTotalWeight(vehicles: PostingVehicleDTO[]) {
  const vehiclesWithWeight = vehicles.filter(
    ({ curb_weight }) => !!curb_weight,
  );
  const totalCurbWeight = vehicles.reduce(
    (total, { curb_weight }) => total + (curb_weight || 0),
    0,
  );

  if (totalCurbWeight === 0) {
    return null;
  }

  const weightText = `~ ${formatNumber(totalCurbWeight)} ${vehicles[0]?.curb_weight_unit || 'lbs'}`;

  return vehiclesWithWeight.length !== vehicles.length ? (
    <Box color="Yellow500">
      <Inline space="xsmall" verticalAlign="center">
        <WarningRounded fontSize="small" />

        <Typography color="inherit">
          Total Curb Weight ({vehiclesWithWeight.length} out of{' '}
          {vehicles.length} {formatVehicleLength(vehicles.length)})
        </Typography>

        <TextBox>{weightText}</TextBox>
      </Inline>
    </Box>
  ) : (
    <Inline space="xsmall" verticalAlign="center">
      <WeightIcon fontSize="small" htmlColor={Color.Dark100} />

      <Typography aria-label="Total Curb Weight">
        Total Curb Weight: {weightText}
      </Typography>
    </Inline>
  );
}

interface PostingVehiclesProps {
  load: PostingLoadDTO;
  isExpanded: boolean;
}

export function PostingVehicles({
  isExpanded,
  load: { vehicles, transport_type, hide_vehicle_info },
}: PostingVehiclesProps) {
  const shouldShowDetails = vehicles.length === 1 || isExpanded;

  return (
    <Stack space="small">
      <ScrollableViewport
        BoxProps={{
          maxHeight: MobileAppBridge.isInjected() ? undefined : '325px',
        }}
        render={(ref) => (
          <Stack
            space={
              shouldShowDetails
                ? MobileAppBridge.isInjected()
                  ? 'medium'
                  : 'small'
                : 'xxsmall'
            }
          >
            {vehicles.map((vehicle, index) => (
              <PostingVehicle
                key={vehicle.guid}
                vehicle={vehicle}
                showDetails={shouldShowDetails}
                transportType={transport_type}
                hideVehicleInfo={!!hide_vehicle_info}
                ref={vehicles.length === index + 1 ? ref : undefined}
              />
            ))}
          </Stack>
        )}
      />

      {vehicles.length > 1 && renderTotalWeight(vehicles)}
    </Stack>
  );
}

interface PostingVehiclesExpanderProps {
  vehicles: PostingVehicleDTO[];
  render: (props: {
    isExpanded: boolean;
    heading: ReactElement;
  }) => ReactElement;
}

export function PostingVehiclesExpander({
  render,
  vehicles,
}: PostingVehiclesExpanderProps) {
  const [isExpanded, setExpandedState] = useState(false);

  const heading = (
    <Columns align="center">
      <Column>
        <Typography variant="inherit">
          {vehicles.length} {formatVehicleLength(vehicles.length)}
        </Typography>
      </Column>

      {vehicles.length > 1 && (
        <Column width="content">
          <Link
            href="#"
            color="primary"
            variant="body2"
            onClick={() => {
              trackLoadboardEvent({
                name: 'SLB: UI Interaction',
                type: isExpanded
                  ? 'Vehicle list: Clicked Hide'
                  : 'Vehicle list: Clicked Show',
              });
              setExpandedState((prev) => !prev);
            }}
          >
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </Link>
        </Column>
      )}
    </Columns>
  );

  return renderChildren(render({ isExpanded, heading }));
}
